import { useState, useEffect } from 'react'
import { useAppSelector } from '../hooks/redux'
import { usePermissions, Permission } from '../hooks/usePermissions'
import { getApiUrl, createFetchConfig } from '../config/api'
import LoadingOverlay from '../components/LoadingOverlay'
import { 
  CogIcon,
  BuildingOfficeIcon,
  PaintBrushIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

interface SystemSettingsData {
  [key: string]: {
    value: string
    category: string
    description: string
    updatedAt: string
    updatedBy: string
  }
}

interface TabConfig {
  id: string
  name: string
  icon: React.ComponentType<{ className?: string }>
  description: string
}

const SystemSettings = () => {
  const { user } = useAppSelector(state => state.auth)
  const { hasPermission } = usePermissions()
  
  const [settings, setSettings] = useState<SystemSettingsData>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('appearance')
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [originalSettings, setOriginalSettings] = useState<SystemSettingsData>({})
  const [isReloading, setIsReloading] = useState(false)

  // 檢查權限
  if (!hasPermission(Permission.MANAGE_SYSTEM_SETTINGS) && user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">權限不足</h1>
          <p className="mt-2 text-gray-600">您沒有權限存取系統設定頁面</p>
        </div>
      </div>
    )
  }

  const tabs: TabConfig[] = [
    {
      id: 'appearance',
      name: '外觀設定',
      icon: PaintBrushIcon,
      description: '系統標題、Logo 和主題設定'
    },
    {
      id: 'basic',
      name: '基本設定',
      icon: BuildingOfficeIcon,
      description: '組織資訊和系統基本配置'
    },
    {
      id: 'security',
      name: '安全設定',
      icon: ShieldCheckIcon,
      description: '認證方式和安全政策'
    }
  ]

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const response = await fetch(getApiUrl('/system/settings'), createFetchConfig())
      const data = await response.json()
      
      if (data.success) {
        setSettings(data.data)
        setOriginalSettings(data.data)
      } else {
        setMessage({ type: 'error', text: data.message || '載入設定失敗' })
      }
    } catch (error) {
      console.error('Fetch settings error:', error)
      setMessage({ type: 'error', text: '載入設定失敗' })
    } finally {
      setLoading(false)
    }
  }

  const handleSettingChange = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value
      }
    }))
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      // 只傳送有變更的設定值
      const changedSettings: Record<string, string> = {}
      Object.keys(settings).forEach(key => {
        if (settings[key]?.value !== originalSettings[key]?.value) {
          changedSettings[key] = settings[key].value
        }
      })

      const response = await fetch(getApiUrl('/system/settings'), {
        ...createFetchConfig(),
        method: 'PUT',
        body: JSON.stringify({ settings: changedSettings })
      })
      
      const data = await response.json()
      
      if (data.success) {
        setMessage({ type: 'success', text: '設定儲存成功' })
        setOriginalSettings(settings)
        setHasChanges(false)
        
        // 如果更新了網站標題、側邊欄標題或 Logo，重新整理頁面以反映變更
        if (changedSettings['system.title'] || changedSettings['system.sidebar_title'] || changedSettings['system.logo'] || changedSettings['system.sidebar_logo']) {
          setTimeout(() => {
            setIsReloading(true)
            // 稍微延遲重新載入，讓使用者看到載入效果
            setTimeout(() => {
              window.location.reload()
            }, 500)
          }, 1000)
        }
      } else {
        setMessage({ type: 'error', text: data.message || '儲存失敗' })
      }
    } catch (error) {
      console.error('Save settings error:', error)
      setMessage({ type: 'error', text: '儲存失敗' })
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setSettings(originalSettings)
    setHasChanges(false)
    setMessage(null)
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const file = e.target.files?.[0]
    if (!file) return

    // 檢查檔案大小 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: '檔案大小不能超過 10MB' })
      return
    }

    // 檢查檔案類型
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: '只能上傳圖片檔案' })
      return
    }

    try {
      setSaving(true)
      setMessage(null)

      const formData = new FormData()
      formData.append('file', file)

      // 根據不同的 key 選擇不同的上傳端點
      const uploadEndpoint = key === 'system.sidebar_logo' ? '/upload/sidebar-logo' : '/upload/system-logo'
      
      const response = await fetch(getApiUrl(uploadEndpoint), {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      const data = await response.json()

      if (data.success) {
        // 更新設定值
        handleSettingChange(key, data.url)
        const logoType = key === 'system.sidebar_logo' ? '側邊欄 Logo' : '系統 Logo'
        setMessage({ type: 'success', text: `${logoType} 上傳成功，正在更新頁面...` })
        
        // 清除檔案輸入
        e.target.value = ''
        
        // 顯示重新載入效果，然後重新載入頁面
        setTimeout(() => {
          setIsReloading(true)
          // 稍微延遲重新載入，讓使用者看到載入效果
          setTimeout(() => {
            window.location.reload()
          }, 500)
        }, 1000)
      } else {
        setMessage({ type: 'error', text: data.error || 'Logo 上傳失敗' })
      }
    } catch (error) {
      console.error('Logo upload error:', error)
      setMessage({ type: 'error', text: 'Logo 上傳失敗' })
    } finally {
      setSaving(false)
    }
  }

  const getSettingsByCategory = (category: string) => {
    return Object.entries(settings).filter(([_, setting]) => setting.category === category)
  }

  const renderSettingField = (key: string, setting: any) => {
    const isBoolean = setting.value === 'true' || setting.value === 'false'
    
    if (isBoolean) {
      return (
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700">
              {setting.description}
            </label>
            <p className="text-xs text-gray-500 mt-1">{key}</p>
          </div>
          <button
            type="button"
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              setting.value === 'true' ? 'bg-blue-600' : 'bg-gray-200'
            }`}
            onClick={() => handleSettingChange(key, setting.value === 'true' ? 'false' : 'true')}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                setting.value === 'true' ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      )
    }

    // Logo 上傳特殊處理 (系統 Logo 和側邊欄 Logo)
    if (key === 'system.logo' || key === 'system.sidebar_logo') {
      const logoType = key === 'system.sidebar_logo' ? '側邊欄 Logo' : '系統 Logo'
      const placeholderText = key === 'system.sidebar_logo' ? '輸入側邊欄 Logo 圖片 URL 或使用下方上傳功能' : '輸入系統 Logo 圖片 URL 或使用下方上傳功能'
      
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {setting.description}
          </label>
          
          {/* 當前 Logo 預覽 */}
          {setting.value && (
            <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <p className="text-sm text-gray-600 mb-2">目前 {logoType}：</p>
              <img 
                src={setting.value.startsWith('http') ? setting.value : `${window.location.origin}${setting.value}`}
                alt={logoType} 
                className="max-h-16 max-w-32 object-contain border border-gray-300 rounded"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            </div>
          )}
          
          {/* URL 輸入框 */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-600 mb-1">
              {logoType} URL
            </label>
            <input
              type="text"
              value={setting.value}
              onChange={(e) => handleSettingChange(key, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder={placeholderText}
            />
          </div>
          
          {/* 檔案上傳 */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-600 mb-1">
              或上傳 {logoType} 檔案
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleLogoUpload(e, key)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <p className="text-xs text-gray-500 mt-1">支援 JPG, PNG, GIF, SVG 格式，最大 10MB</p>
          </div>
          
          <p className="text-xs text-gray-500">{key}</p>
        </div>
      )
    }

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {setting.description}
        </label>
        <input
          type="text"
          value={setting.value}
          onChange={(e) => handleSettingChange(key, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder={setting.description}
        />
        <p className="text-xs text-gray-500 mt-1">{key}</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <>
      {/* 載入遮罩 */}
      <LoadingOverlay 
        isVisible={isReloading} 
        message={`系統設定更新中`} 
      />
      
      <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-2">
            <CogIcon className="h-8 w-8 text-gray-900" />
            <h1 className="text-3xl font-bold text-gray-900">系統設定</h1>
          </div>
          <p className="text-gray-600">管理系統的外觀、功能和安全設定</p>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-md ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            <div className="flex items-center">
              {message.type === 'success' ? (
                <CheckIcon className="h-5 w-5 mr-2" />
              ) : (
                <XMarkIcon className="h-5 w-5 mr-2" />
              )}
              {message.text}
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-1/4">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-left rounded-md text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={`mr-3 h-5 w-5 ${
                      activeTab === tab.id ? 'text-blue-500' : 'text-gray-400'
                    }`} />
                    <div>
                      <div>{tab.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{tab.description}</div>
                    </div>
                  </button>
                )
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  {tabs.find(tab => tab.id === activeTab)?.name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {tabs.find(tab => tab.id === activeTab)?.description}
                </p>
              </div>

              <div className="px-6 py-6">
                <div className="space-y-6">
                  {getSettingsByCategory(activeTab).map(([key, setting]) => (
                    <div key={key}>
                      {renderSettingField(key, setting)}
                    </div>
                  ))}
                  
                  {getSettingsByCategory(activeTab).length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      此分類暫無可用設定
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              {hasChanges && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    重設
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
                  >
                    {saving && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    {saving ? '儲存中...' : '儲存變更'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default SystemSettings
